/**
 * Chart Theme Configuration
 * Centralised theme settings for all tracker charts.
 *
 * All values are CSS-variable references that resolve at SVG render time —
 * charts adapt automatically when the theme toggles. The OKLCH wrappers are
 * required because the tokens in `src/index.css` store channel triplets only
 * (e.g. `--chart-1: 0.445 0.105 51.268`), not full color strings.
 */

// Primary metric colours — mapped to the canonical earthy chart palette.
export const CHART_COLORS = {
  heartRate: "oklch(var(--chart-1))",
  bloodPressure: "oklch(var(--chart-2))",
  bloodGlucose: "oklch(var(--chart-3))",
  temperature: "oklch(var(--chart-4))",
  respiratoryRate: "oklch(var(--chart-5))",
  weight: "oklch(var(--chart-6))",
  waist: "oklch(var(--chart-3))",
  hip: "oklch(var(--chart-4))",

  // Grid and axis — neutral chrome.
  grid: "oklch(var(--border))",
  axis: "oklch(var(--border))",
  tick: "oklch(var(--muted-foreground))",

  // Tooltip — matches popover surface.
  tooltipBg: "oklch(var(--popover))",
  tooltipBorder: "oklch(var(--border))",
  tooltipText: "oklch(var(--popover-foreground))",
  tooltipMuted: "oklch(var(--muted-foreground))",

  // Reference ranges — semantic tokens at 20% alpha.
  normalRange: "oklch(var(--success) / 0.2)",
  warningRange: "oklch(var(--warning) / 0.2)",
  dangerRange: "oklch(var(--destructive) / 0.2)",
};

// Blood glucose sample type colours — meal periods + alert.
export const BLOOD_GLUCOSE_COLORS: Record<string, string> = {
  Fasting: "oklch(var(--chart-1))",
  "Post-Breakfast": "oklch(var(--chart-1))",
  "Pre-Lunch": "oklch(var(--chart-3))",
  "Post-Lunch": "oklch(var(--chart-3))",
  "Pre-Dinner": "oklch(var(--chart-4))",
  "Post-Dinner": "oklch(var(--chart-4))",
  "3 AM": "oklch(var(--muted-foreground))",
  Random: "oklch(var(--destructive))",
};

// Chart container config.
export const CHART_CONFIG = {
  margin: { top: 10, right: 10, left: 10, bottom: 10 },
  gridDashArray: "4 4",
  dotRadius: 5,
  activeDotRadius: 7,
  strokeWidth: 2.5,
};

// Tooltip styles using semantic classes.
export const TOOLTIP_STYLES = {
  container: "bg-popover border border-border rounded-lg shadow-xl p-3",
  title: "text-sm font-medium text-foreground",
  value: "text-lg font-bold text-foreground",
  label: "text-xs text-muted-foreground mt-1",
  muted: "text-xs text-muted-foreground",
};

// Get colour for metric type.
export function getMetricColor(metricId: string): string {
  const colors: Record<string, string> = {
    "heart-rate": CHART_COLORS.heartRate,
    "blood-pressure": CHART_COLORS.bloodPressure,
    "blood-glucose": CHART_COLORS.bloodGlucose,
    temperature: CHART_COLORS.temperature,
    "respiratory-rate": CHART_COLORS.respiratoryRate,
    weight: CHART_COLORS.weight,
    "waist-size": CHART_COLORS.waist,
    hip: CHART_COLORS.hip,
  };
  return colors[metricId] || CHART_COLORS.heartRate;
}
